import {useCallback, useEffect, useRef, useState} from 'react';
import {isEqual as lodashIsEqual} from 'lodash';

// Event-driven localStorage sync model:
// - cross-tab updates come from native `storage` events
// - same-tab writes don't emit `storage`, so we emit a custom event
//   from patched Storage APIs (`setItem`, `removeItem`, `clear`)
//
// The prototype patch is intentional: React components need to re-read
// localStorage when another part of the *same tab* writes (e.g. debug flags in
// DevTools). Without this, only cross-tab `storage` events would refresh hooks.
//
// Detection / coexistence:
// - We install at most once per page load (`__INSCOPE_STORAGE_NOTIFIER_V1__` on
//   Storage.prototype) and store the original native methods for inspection.
// - If another script wrapped Storage first, we chain: our wrapper calls the
//   current `setItem`/`removeItem`/`clear` we captured at install time, so
//   ordering is preserved for that snapshot.
// - If a later script *replaces* `Storage.prototype.setItem` (etc.) without
//   calling the previous implementation, our same-tab notifier is bypassed
//   until reload. Chained wrappers preserve us. Cross-tab `storage` events
//   are unaffected (other tabs still refresh subscribers).
// - Named wrapper functions (`inscopeStorageNotifierSetItem`, …) make stack
//   traces and heap snapshots identifiable vs generic "patchedSetItem".
//
// Why this is better than polling:
// - zero periodic wakeups while idle
// - updates happen immediately at write-time
// - avoids repeated parse/compute work on intervals
// - scales better when many subscribers are mounted
const LOCAL_STORAGE_CHANGE_EVENT = 'inscope:local-storage-change';
const STORAGE_NOTIFIER_PATCH_KEY = '__INSCOPE_STORAGE_NOTIFIER_V1__';
let isStorageNotifierInstalled = false;
let hasStorageNotifierInstallFailed = false;

const emitLocalStorageChange = (key = null) => {
  if (typeof window === 'undefined') return;
  try {
    const event = new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
      detail: {key},
    });
    window.dispatchEvent(event);
  } catch (_error) {
    // Best-effort notification only. If custom events are unavailable/restricted,
    // we still retain cross-tab updates via native `storage` events.
  }
};

const installStorageNotifier = () => {
  if (
    isStorageNotifierInstalled ||
    hasStorageNotifierInstallFailed ||
    typeof window === 'undefined'
  ) {
    return;
  }
  const storageProto = window.Storage?.prototype;
  if (!storageProto) return;

  if (storageProto[STORAGE_NOTIFIER_PATCH_KEY]) {
    isStorageNotifierInstalled = true;
    return;
  }

  try {
    const originalSetItem = storageProto.setItem;
    const originalRemoveItem = storageProto.removeItem;
    const originalClear = storageProto.clear;
    if (
      typeof originalSetItem !== 'function' ||
      typeof originalRemoveItem !== 'function' ||
      typeof originalClear !== 'function'
    ) {
      hasStorageNotifierInstallFailed = true;
      return;
    }

    function inscopeStorageNotifierSetItem(key, value) {
      const normalizedKey = String(key);
      const result = originalSetItem.call(this, key, value);
      emitLocalStorageChange(normalizedKey);
      return result;
    }

    function inscopeStorageNotifierRemoveItem(key) {
      const normalizedKey = String(key);
      const result = originalRemoveItem.call(this, key);
      emitLocalStorageChange(normalizedKey);
      return result;
    }

    function inscopeStorageNotifierClear() {
      const result = originalClear.call(this);
      emitLocalStorageChange(null);
      return result;
    }

    storageProto.setItem = inscopeStorageNotifierSetItem;
    storageProto.removeItem = inscopeStorageNotifierRemoveItem;
    storageProto.clear = inscopeStorageNotifierClear;

    Object.defineProperty(storageProto, STORAGE_NOTIFIER_PATCH_KEY, {
      value: {
        originals: {
          setItem: originalSetItem,
          removeItem: originalRemoveItem,
          clear: originalClear,
        },
      },
      enumerable: false,
      configurable: true,
    });

    isStorageNotifierInstalled = true;
  } catch (_error) {
    // Guard against environments that forbid prototype mutation.
    // Hook remains functional for cross-tab updates via `storage`.
    hasStorageNotifierInstallFailed = true;
  }
};

const shouldRefreshForKey = (watchedKey, changedKey) =>
  changedKey == null || changedKey === watchedKey;

/**
 * Subscribe to a localStorage key and derive a memoized value.
 *
 * @param {string} key
 * @param {() => unknown} readSnapshot
 * @param {{isEqual?: (a: unknown, b: unknown) => boolean}} [options]
 * @returns {unknown}
 */
export const useLocalStorage = (key, readSnapshot, options = {}) => {
  const compare = options.isEqual || lodashIsEqual;
  const readSnapshotRef = useRef(readSnapshot);
  readSnapshotRef.current = readSnapshot;

  const [value, setValue] = useState(() => readSnapshotRef.current());

  const refreshValue = useCallback(() => {
    const next = readSnapshotRef.current();
    setValue((prev) => (compare(prev, next) ? prev : next));
  }, [compare]);

  useEffect(() => {
    // Ensure same-tab write notifications are installed once globally.
    installStorageNotifier();
    refreshValue();

    // Cross-tab updates (native browser event).
    const onStorage = (event) => {
      if (shouldRefreshForKey(key, event?.key ?? null)) {
        refreshValue();
      }
    };

    // Same-tab updates (custom event emitted by patched Storage APIs).
    const onSameTabStorageWrite = (event) => {
      const changedKey = event?.detail?.key ?? null;
      if (shouldRefreshForKey(key, changedKey)) {
        refreshValue();
      }
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, onSameTabStorageWrite);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(
        LOCAL_STORAGE_CHANGE_EVENT,
        onSameTabStorageWrite
      );
    };
  }, [key, refreshValue]);

  return value;
};
